import type { RefObject } from 'react';
import React, { Component, createRef, Suspense } from 'react';

import { Version } from './../ricos-content/index.js';
import type { RichContent } from './../ricos-schema/legacy/wix/rich_content/v1/index.js';
import type { BICallbacks, SEOSettings } from './../ricos-types/index.js';
import { addDevToolsClickListener } from './../wix-rich-content-common/index.js';
import {
  RicosDevToolsLazy,
  shouldShowDebuggingInfo,
} from './../wix-rich-content-common/lib/devTools.js';
import { getContentSummary } from './../wix-rich-content-common/lib/ricosContentAnalytics.js';

import { ContentViewer } from './ContentViewer/index.js';
import type { RicosViewerProps } from './types.js';
import { mergeViewerProps } from './utils.js';

interface State {
  error?: string;
  TextSelectionToolbar?: $TSFixMe;
  LinkPreviewPopover?: $TSFixMe;
  debugInvokedWithMouse?: boolean;
  content?: RichContent;
}

class RicosViewerNext extends Component<RicosViewerProps, State> {
  viewerRef!: RefObject<any>;

  getBiCallback: <T extends keyof BICallbacks>(key: T) => BICallbacks[T] | undefined;

  static displayName = 'RicosViewerNext';

  static getDerivedStateFromError(error: string) {
    return { error };
  }

  componentDidCatch(error: $TSFixMe, errorInfo: $TSFixMe) {
    console.error({ error, errorInfo });
  }

  constructor(props: RicosViewerProps) {
    super(props);
    this.getBiCallback = key => {
      const { children, _rcProps } = this.props;
      return children?.props.helpers?.[key] || _rcProps?.helpers?.[key];
    };
    this.viewerRef = createRef();
    this.state = {};
  }

  static defaultProps = {
    onError: (err: $TSFixMe) => {
      throw err;
    },
  };

  componentDidMount() {
    const onViewerLoaded = this.getBiCallback('onViewerLoaded');
    const content = this.props.content || this.props._rcProps?.content;
    const { pluginsCount } = (content && getContentSummary(content)) || {};
    pluginsCount &&
      onViewerLoaded?.({
        isPreview: false,
        version: Version.currentVersion,
        pluginsCount,
        url: document.URL,
        contentId: content?.metadata?.id,
      });

    addDevToolsClickListener(this.viewerRef.current, () =>
      this.setState({ debugInvokedWithMouse: true })
    );
  }

  loadTextSelection = () => {
    const { textSelectionToolbar, isMobile } = this.props;
    const needTextSelectionToolbar = textSelectionToolbar && !isMobile;
    if (needTextSelectionToolbar && !this.state.TextSelectionToolbar) {
      const TextSelectionToolbar = React.lazy(() => import('./TextSelectionToolbar.js'));
      this.setState({ TextSelectionToolbar });
    }
  };

  loadLinkPreviewPopover = () => {
    const { linkPreviewPopoverFetchData, isMobile } = this.props;
    const needLoadLinkPreviewPopover = linkPreviewPopoverFetchData && !isMobile;
    if (needLoadLinkPreviewPopover && !this.state.LinkPreviewPopover) {
      const LinkPreviewPopover = React.lazy(() => import('./LinkPreviewPopover.js'));
      this.setState({ LinkPreviewPopover });
    }
  };

  onMouseOver = () => {
    this.loadTextSelection();
    this.loadLinkPreviewPopover();
  };

  render() {
    const { seoSettings, linkPreviewPopoverFetchData, mediaSettings, content, debugMode } =
      this.props;
    const { TextSelectionToolbar, LinkPreviewPopover } = this.state;
    const mergedProps = mergeViewerProps({
      ...this.props,
      viewerProps: {
        seoMode: seoSettings as SEOSettings | undefined,
        onMouseOver: this.onMouseOver,
        locale: this.props.locale,
      },
    });

    try {
      if (this.state.error) {
        this.props.onError?.(this.state.error);
        return null;
      }
      return [
        <ContentViewer
          {...mergedProps}
          content={this.state.content ?? mergedProps.content}
          config={mergedProps.config}
          onHover={() => {}}
          key="viewer"
          setRef={this.viewerRef}
          fullscreenProps={mediaSettings?.fullscreenProps}
          devTools={
            this.state.debugInvokedWithMouse ||
            shouldShowDebuggingInfo(debugMode?.includes('all')) ? (
              <React.Suspense>
                <RicosDevToolsLazy
                  schema={{
                    getContent: () => (this.state.content ?? mergedProps.content) as RichContent,
                    setContent: content => this.setState({ content }),
                  }}
                  version={Version.currentVersion}
                  mode="viewer"
                  contentState={content}
                  ricosProps={this.props}
                  autoOpen={this.state.debugInvokedWithMouse ?? false}
                />
              </React.Suspense>
            ) : null
          }
        />,
        TextSelectionToolbar ? (
          <Suspense key="TextSelectionToolbar" fallback={<div />}>
            <TextSelectionToolbar
              onButtonClick={this.getBiCallback('onViewerAction')}
              container={this.viewerRef.current}
            />
          </Suspense>
        ) : null,
        LinkPreviewPopover ? (
          <Suspense key="LinkPreviewPopover" fallback={<div />}>
            <LinkPreviewPopover
              fetchUrlPreviewData={linkPreviewPopoverFetchData}
              container={this.viewerRef.current}
            />
          </Suspense>
        ) : null,
      ];
    } catch (e) {
      // @ts-ignore $TSFixMe
      this.props.onError?.(e);
      return null;
    }
  }
}

export default RicosViewerNext;
